import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';
import '../../library/editors/index.js';
import '../../library/fields/index.js';
import '../../library/lists/d-list-section-attachment.js';
import './d-edit-rotation';
import { EntityContent, toLookup } from '../entity-content.js';
import type { DEditRotation } from './d-edit-rotation.js';
import './d-view-rotation-schedule.js';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { RotationEmployeeWeek } from './rotation-employee-week';
import type { FunctionView } from './function-view';
import type { FunctionViewEditItem } from './function-view-edit-item';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 */
@customElement('d-function-view')
export class DFunctionView extends EntityContent<FunctionView, FunctionViewEditItem> {
  static readonly styles = EntityContent.styles;
  private static get functionTypeLookup() {
    return toLookup({
      SINGLE: t('functions.typeSingle'),
      ROTATED: t('functions.typeRotated'),
      SIMULTANEOUS: t('functions.typeSimultaneous'),
    });
  }
  private static get functionTypeOptions() {
    return [
      { value: 'SINGLE', text: t('functions.typeSingle') },
      { value: 'ROTATED', text: t('functions.typeRotated') },
      {
        value: 'SIMULTANEOUS',
        text: t('functions.typeSimultaneous'),
      },
    ];
  }
  @query('#rotations')
  rotations!: DEditRotation;
  @property({ type: Object })
  organization: any = {};
  @property({ type: String })
  entityType = 'functions';
  @property({ type: String })
  employeeId = '';
  @property({ type: Array })
  employees = [];
  @property({ type: Array })
  tasks = [];

  protected get preventDelete(): boolean {
    return this.entityView.isCommon || this.entityView.isGeneralManager;
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('functions', this.viewFields(), this.entityView)} `;
  }

  renderRotationSchedule(): TemplateResult<1> {
    return html`
      ${this.entityView.rotation === undefined
        ? nothing
        : html` <d-section>
            <d-view-rotation-schedule
              .count=${this.entityView.rotation.count}
              .employees=${this.entityView.rotation.employees}
              .rotationType=${this.entityView.rotation.type}
              .rotations=${this.entityView.rotation.rotations}
              .selectedEmployees=${this.entityView.rotation.selectedEmployees}
              .today=${LocalDate.fromString(this.today)}
            >
            </d-view-rotation-schedule
          ></d-section>`}
    `;
  }

  renderAssignedTasks(): TemplateResult<1> {
    return html` <d-list-section
      .items=${this.entityView.assignedTasks}
      page-id="${this.pageId}"
      employee-id="${this.employeeId}"
      field="functions_ownTasks"
      icon="tasks"
      .writeAccess="${this.entityView.currentUserHasWriteAccess}"
      .contentStickyTop=${this.contentStickyTop}
    >
    </d-list-section>`;
  }

  renderResponsibleTasks(): TemplateResult<1> {
    return html`<d-list-section
      .items=${this.entityView.responsibleTasks}
      .limitItems=${3}
      page-id="${this.pageId}"
      icon="tasks"
      employee-id="${this.employeeId}"
      field="tasks_responsible"
      .writeAccess="${this.entityView.currentUserHasWriteAccess}"
      .contentStickyTop=${this.contentStickyTop}
    >
    </d-list-section>`;
  }

  async initializeEditItem() {
    this.editItem = {
      functionType: this.entityView.functionType,
      rotationType: this.entityView.rotation === undefined ? 'R-0' : this.entityView.rotation.type,
      rotations: this.entityView.rotation?.rotations || [],
      description: this.entityView.description,
      employees: this.entityView.employees,
      pages: this.entityView.pages,
      name: this.entityView.name,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: FunctionViewEditItem): TemplateResult<1> {
    return html` ${this.renderFieldSection('functions', this.editFields(item), item)}`;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'description',
        type: 'view-html',
        class: 'fullWidth',
      },
      {
        field: 'functionType',
        type: 'view-text',
        options: DFunctionView.functionTypeOptions,
        class: 'minWidth300',
        condition: this.entityView.functionType !== 'COMMON',
      },
      {
        field: 'assignedEmployeeLinks',
        fieldForLabel: 'assignedEmployeeNames',
        type: 'view-html',
        class: 'minWidth300',
        condition: this.entityView.functionType !== 'COMMON',
      },
      {
        field: 'rotations',
        type: 'custom',
        render: () => this.renderRotationSchedule(),
        condition: this.entityView.rotation !== undefined,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'functions_ownTasks',
        type: 'custom',
        icon: 'tasks',
        items: 'assignedTasks',
        render: () => this.renderAssignedTasks(),
      },
      {
        field: 'functions_ownTasks',
        icon: 'tasks',
        items: 'assignedTasks',
        condition: false,
      },
      {
        field: 'tasks_responsible',
        type: 'custom',
        icon: 'tasks',
        items: 'responsibleTasks',
        render: () => this.renderResponsibleTasks(),
        condition: this.entityView.responsibleTasks.length > 0,
      },
      {
        field: 'tasks_responsible',
        icon: 'tasks',
        items: 'responsibleTasks',
        condition: false,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
      {
        field: 'generalFields_attachments',
        icon: 'attachments',
        items: 'attachments',
        condition: false,
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private editFields(item: FunctionViewEditItem): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        placeholder: t('reports.fieldMustBeFilledOut'),
        autofocus: !this.entityView.isConfirmedEntity,
        selectOnFocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        class: 'fullWidth',
      },
      {
        field: 'description',
        type: 'edit-html',
        docsForLinking: this.entityView.docsForLinking,
      },
      {
        field: 'functionType',
        type: 'edit-dropdown',
        options: DFunctionView.functionTypeOptions,
        disabled: this.entityView.responsibilityLocked,
        class: 'fullWidth',
        condition: this.entityView.functionType !== 'COMMON',
      },
      {
        field: 'employees',
        type: 'edit-tags',
        options: this.entityView.availableEmployees,
        single: item.functionType === 'SINGLE',
        class: 'fullWidth',
        condition: this.entityView.functionType !== 'COMMON',
      },
      {
        type: 'custom',
        render: () => this.renderEditRotation(item),
        condition: item.functionType === 'ROTATED' && item.employees.length > 1,
      },
    ];
  }

  private renderEditRotation(item: FunctionViewEditItem) {
    return html`<d-edit-rotation
      .employees=${this.entityView.availableEmployees}
      id="rotations"
      .rotationType=${item.rotationType}
      .rotations=${item.rotations}
      .selectedEmployees=${this.entityView.availableEmployees
        .filter((e) => item.employees.includes(e.value))
        .map((e) => ({ uuid: e.value, name: e.text }))}
      @rotation-changed=${(e: CustomEvent<{ rotationType: string; rotations: RotationEmployeeWeek[] }>) => {
        this.editItem = { ...item, rotationType: e.detail.rotationType, rotations: e.detail.rotations };
      }}
    >
    </d-edit-rotation>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-function-view': DFunctionView;
  }
}
